import { Injectable, inject, signal, computed, type Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticationService,
  type FirebaseAuthResponse,
  type AuthenticatedUser,
} from '@hapnest/admin-sdk';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Re-export SDK types for consumers
export type { AuthenticatedUser, FirebaseAuthResponse } from '@hapnest/admin-sdk';

/**
 * Authentication state management service using Angular Signals.
 *
 * Uses the Admin SDK's AuthenticationService directly for backend communication
 * and Signals for reactive state management.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authApi = inject(AuthenticationService);
  private readonly router = inject(Router);

  private readonly currentUserSignal = signal<AuthenticatedUser | null>(null);

  private readonly sessionTokenSignal = signal<string | null>(
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('session_token')
      : null,
  );

  readonly sessionToken: Signal<string | null> =
    this.sessionTokenSignal.asReadonly();

  readonly currentUser: Signal<AuthenticatedUser | null> =
    this.currentUserSignal.asReadonly();

  readonly isAuthenticated: Signal<boolean> = computed(
    () => this.currentUserSignal() !== null,
  );

  readonly isAdmin: Signal<boolean> = computed(() => {
    const user = this.currentUserSignal();
    return user?.admin === true;
  });

  async loginWithFirebase(idToken: string): Promise<FirebaseAuthResponse> {
    if (!idToken || idToken.trim().length === 0) {
      throw new Error('Firebase ID token is required for authentication');
    }

    try {
      const response = await firstValueFrom(
        this.authApi.authenticateWithFirebase({
          firebaseAuthRequest: { idToken },
        }),
      );

      if (response.user) {
        this.updateAuthenticationState(response.user);
        if (response.sessionToken) {
          this.sessionTokenSignal.set(response.sessionToken);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('session_token', response.sessionToken);
          }
        }
      } else {
        throw new Error(
          'Authentication succeeded but no user data was returned',
        );
      }

      return response;
    } catch (error) {
      this.clearAuthenticationState();
      throw this.enhanceAuthError(error, 'Login');
    }
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.authApi.logout().pipe(
          catchError((error) => {
            console.error('Logout API call failed:', error);
            return of(undefined);
          }),
        ),
      );
    } finally {
      this.clearAuthenticationState();
    }
  }

  async checkAuthStatus(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.authApi.getProfile().pipe(
          catchError(() => [null]),
        ),
      );

      if (response && response.user) {
        this.updateAuthenticationState(response.user);
        if (response.sessionToken) {
          this.sessionTokenSignal.set(response.sessionToken);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('session_token', response.sessionToken);
          }
        }
        return true;
      }

      this.clearAuthenticationState();
      return false;
    } catch {
      this.clearAuthenticationState();
      return false;
    }
  }

  private updateAuthenticationState(user: AuthenticatedUser): void {
    this.currentUserSignal.set(user);
  }

  private clearAuthenticationState(): void {
    this.currentUserSignal.set(null);
    this.sessionTokenSignal.set(null);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('session_token');
    }
  }

  private enhanceAuthError(error: unknown, operation: string): Error {
    if (error instanceof Error) {
      const httpError = error as { status?: number };

      if (httpError.status === 401) {
        return new Error(
          `${operation} failed: Invalid or expired authentication token. ` +
            `Please sign in again.`,
        );
      }

      if (httpError.status === 403) {
        return new Error(
          `${operation} failed: Access denied. ` +
            `You do not have permission to access this application.`,
        );
      }

      if (httpError.status === 500) {
        return new Error(
          `${operation} failed: Server error. ` +
            `Please try again later or contact support if the problem persists.`,
        );
      }

      if (httpError.status === 0 || error.message.includes('network')) {
        return new Error(
          `${operation} failed: Network error. ` +
            `Please check your internet connection and try again.`,
        );
      }

      return new Error(`${operation} failed: ${error.message}`);
    }

    return new Error(`${operation} failed: An unexpected error occurred`);
  }
}
